import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { eur, num } from '../../lib/api';

/**
 * Payback chart: linha de caixa operacional acumulada ao longo de 36 meses,
 * com linha horizontal do preço de aquisição e marcador vertical do mês
 * em que o payback é atingido. Preço e payback só aparecem se o utilizador
 * estiver autenticado (dependem de valores confidenciais).
 */
export function PaybackChart({ scenario, price, capitalBase, paybackMonths, paybackFirstFull, includeWC, revealPrivate }) {
  if (!scenario?.monthly_cash_series?.length) return null;
  // Prepend a starting point at month 0 with zero cumulative cash so the line
  // starts from the origin instead of the first month's slice.
  const data = [
    { month: 0, cash: 0, label: 'Início' },
    ...scenario.monthly_cash_series.map((p) => ({
      month: p.absolute_month,
      cash: p.cumulative_cash,
      label: `Mês ${p.absolute_month}`,
    })),
  ];
  // Chart, KPI and exports share ONE investment base: acquisition + transaction
  // costs (+ working capital when the toggle is active) and ONE flow sequence.
  const investment = capitalBase ?? scenario.capital_total_eur ?? price;
  const paybackMonth = paybackMonths ?? scenario.payback_total_capital_months;
  const firstFull = paybackFirstFull ?? scenario.payback_total_first_full_month;
  const showPayback = revealPrivate && Number.isFinite(paybackMonth) && investment;
  const showPriceLine = revealPrivate && investment;

  return (
    <div className="payback-chart" data-testid="payback-chart">
      <div className="payback-chart-header">
        <h4>Caixa operacional acumulada · {scenario.label}</h4>
        <p className="hint">
          Evolução da caixa mês a mês nos 3 anos do cenário.{' '}
          {revealPrivate
            ? `Linha do investimento (aquisição + transação${includeWC ? ' + fundo de maneio' : ''}) e mês de payback assinalados — a mesma base usada nos indicadores.`
            : 'Payback e linha do investimento apenas visíveis dentro do data room.'}
        </p>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 24, right: 40, left: 20, bottom: 8 }}>
          <CartesianGrid stroke="#e2e4dd" strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            type="number"
            domain={[0, 36]}
            ticks={[0, 6, 12, 18, 24, 30, 36]}
            tickFormatter={(v) => (v === 0 ? '0' : `M${v}`)}
            tick={{ fontSize: 11, fill: '#4b5c60' }}
            stroke="#c9ccc2"
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#4b5c60' }}
            tickFormatter={(v) => `${num(v / 1000, 0)} k`}
            stroke="#c9ccc2"
          />
          <Tooltip
            contentStyle={{ background: '#fbfaf5', border: '1px solid #d7d9d3', borderRadius: 8, fontSize: 12 }}
            labelFormatter={(v) => (v === 0 ? 'Início' : `Mês ${v}`)}
            formatter={(v) => [eur(v, 0), 'Caixa acumulada']}
          />
          <Line
            type="monotone"
            dataKey="cash"
            stroke="#4f6a2a"
            strokeWidth={2.5}
            dot={false}
            isAnimationActive={false}
          />
          {showPriceLine && (
            <ReferenceLine
              y={investment}
              stroke="#a0530b"
              strokeDasharray="5 4"
              label={{
                value: `Investimento ${eur(investment, 0)}`,
                position: 'right',
                fill: '#a0530b',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
          {showPayback && (
            <ReferenceLine
              x={paybackMonth}
              stroke="#a0530b"
              strokeDasharray="5 4"
              label={{
                value: `Payback · ${num(paybackMonth, 1)} m${firstFull ? ` (recuperado no mês ${firstFull})` : ''}`,
                position: 'top',
                fill: '#a0530b',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
